import { NgModule, ErrorHandler, Injectable } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { LocationStrategy, HashLocationStrategy, CommonModule } from '@angular/common';
import { LoggerService } from './logger.service';
import { CustomErrorHandler } from './errorhandler.provider';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Routes } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatStepperModule } from '@angular/material/stepper';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDrawerContainer, MatDrawer, MatDrawerContent, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter, NativeDateAdapter } from '@angular/material/core';

import { DataTableColumnCellDirective, DataTableColumnDirective, DatatableComponent, NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgxEditorComponent, NgxEditorMenuComponent } from 'ngx-editor';
import { NgxSpinnerModule } from 'ngx-spinner';

import { AppMaterialModule } from './app-modules/app-material.module';
import { UtilModule } from './app-modules/util.module';
import { TableComponent } from './table/table.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { ReportsComponent } from './reports/reports.component';
import { ManagedocumentsComponent } from './managedocuments/managedocuments.component';
import { ManagecollectionsComponent } from './managecollections/managecollections.component';
import { UserDashboardComponent } from './user-dashboard/user-dashboard.component';
import { AdminHelpSupportComponent } from './admin-help-support/admin-help-support.component';
import { UserProjectDetailComponent } from './user-project-detail/user-project-detail.component';
import { DashboardDetailedScreenComponent } from './dashboard-detailed-screen/dashboard-detailed-screen.component';
import { ManageProjectComponent } from './manage-project/manage-project.component';
import { ManageApprovalComponent } from './manage-approval/manage-approval.component';
import { ManageDocumentComponent } from './manage-document/manage-document.component';
import { CreateProjectService } from './services/create-project.service';
import { LoaderInterceptor } from '../loader.interceptor';
import { AuthInterceptor } from '../auth.interceptor';
import { PendingChangesGuard } from './guards/pending-changes.guard';
import { AuthGuard } from './guards/auth.guard';
import { RoleGuard } from './guards/role.guard';
import { ManageCustomDepartmentComponent } from './manage-custom-department/manage-custom-department.component';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, _displayFormat: Object): string {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    
    // Format as MM/DD/YYYY
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  }

  override parse(value: any): Date | null {
    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const month = Number.parseInt(parts[0], 10);
        const day = Number.parseInt(parts[1], 10);
        const year = Number.parseInt(parts[2], 10);
        if (!Number.isNaN(month) && !Number.isNaN(day) && !Number.isNaN(year)) {
          return new Date(year, month - 1, day);
        }
      }
    }
    return super.parse(value);
  }
}

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const ROLES = {
  SUPER_ADMIN: 'cert.superAdmin',
  ADMIN: 'cert.admin',
  USER: 'cert.user',
} as const;

const ADMIN_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN];
const ALL_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.USER];
const SUPER_ADMIN_ONLY = [ROLES.SUPER_ADMIN];

export const routes: Routes = [
  {
    path: '',
    component: TableComponent,
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ADMIN_ROLES },
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      { path: 'dashboard', component: DashboardComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ADMIN_ROLES } },
      { path: 'dashboard/:projectId', component: DashboardDetailedScreenComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ADMIN_ROLES } },
      { path: 'manage-documents', component: ManagedocumentsComponent, canDeactivate: [PendingChangesGuard], canActivate: [AuthGuard, RoleGuard], data: { roles: ADMIN_ROLES } },
      { path: 'manage-documents/:id', component: ManagedocumentsComponent, canDeactivate: [PendingChangesGuard], canActivate: [AuthGuard, RoleGuard], data: { isDraftSave: false, roles: ADMIN_ROLES }},
      { path: 'manage-project', component: ManageProjectComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ADMIN_ROLES } },
      { path: 'managecollections', component: ManagecollectionsComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ADMIN_ROLES } },
      { path: 'manage-document', component: ManageDocumentComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: SUPER_ADMIN_ONLY } },
      { path: 'manage-custom-department', component: ManageCustomDepartmentComponent, canActivate: [AuthGuard, RoleGuard] },
      { path: 'reports', component: ReportsComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ADMIN_ROLES } },
      { path: 'help-support', component: AdminHelpSupportComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ADMIN_ROLES } },
    ],
  },
  {
    path: 'user-dashboard',
    component: UserDashboardComponent,
    canActivate: [AuthGuard, RoleGuard],
    data: { roles: ALL_ROLES },
    children: [
      { path: 'manage-approval', component: ManageApprovalComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ALL_ROLES } },
    ],
  },
  { path: 'user-dashboard/project/:id', component: UserProjectDetailComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: ALL_ROLES } },
  // { path: 'settings', component: DepartmentComponent, canActivate: [AuthGuard, RoleGuard], data: { roles: SUPER_ADMIN_ONLY } },
  { path: '', redirectTo: '/table', pathMatch: 'full' },
];

@NgModule({
  imports: [
    BrowserModule,
    AppMaterialModule,
    UtilModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatTableModule,
    MatInputModule,
    MatSelectModule,
    MatPaginatorModule,
    MatSortModule,
    MatToolbarModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatDialogModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatTabsModule,
    NgxDatatableModule,
    DataTableColumnCellDirective,
    DataTableColumnDirective,
    DatatableComponent,
    MatStepperModule,
    MatExpansionModule,
    RouterModule.forRoot(routes),
    NgxEditorComponent,
    NgxEditorMenuComponent,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    MatMenuModule,
    MatIconModule,
    MatCardModule,
    MatInputModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTooltipModule,
    MatChipsModule,
    MatBadgeModule,
    MatDrawerContainer,
    MatDrawer,
    MatDrawerContent,
    MatSidenavModule,
    MatListModule,
    MatDividerModule,
  ],


  providers: [
    LoggerService,
    CreateProjectService,
    AuthGuard,
    RoleGuard,
    PendingChangesGuard,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  bootstrap: [],
  declarations: [],
})
export class AppModule {}
